import React from "react";
import axios from "axios";
import { formatCooldownTimer } from "../utils/formatters";

const ControlPanel = ({
  autonomousStatus,
  isCooldownActive,
  isRegimeFilterPause,
  autonomousDisplayStatus,
  cooldownRemainingSec,
  subscriptionStatus,
  portfolio,
  pollingBackoffUi,
  startAutonomousTrading,
  stopAutonomousTrading,
  closeAllPositions,
  closingPositions,
  autonomousLoading,
  tradingNotional,
  tradingLeverage,
  setTradingNotional,
  setTradingLeverage,
  tradingCoin,
  setTradingCoin,
  autoRotateCoin,
  autoRotateLoading,
  toggleAutoRotateCoin,
  cutAndTrail,
  cutAndTrailLoading,
  toggleCutAndTrail,
  ctAdaptive,
  ctAdaptiveLoading,
  toggleCtAdaptive,
  hybridExit,
  hybridExitLoading,
  toggleHybridExit,
  supportedCoins,
  maxLeverageByCoin,
  allowedTradeRegimes,
  allowedTradeRegimesLoading,
  toggleAllowedTradeRegime,
  lastStopReason,
  onDismissStopReason,
  liveBotRegime,
  liveBotCoin,
  BACKEND_URL,
}) => {
  const coinList = Array.isArray(supportedCoins) && supportedCoins.length > 0
    ? supportedCoins : ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'HYPE', 'DOGE', 'ADA', 'AVAX', 'LINK'];
  const maxLevs = maxLeverageByCoin || {
    BTC: 40, ETH: 25, SOL: 20, BNB: 10, XRP: 20,
    HYPE: 10, DOGE: 10, ADA: 10, AVAX: 10, LINK: 10,
  };
  // The coin to HIGHLIGHT / show as "Trading X". While the bot is running
  // (especially with auto-rotate ON), the engine may be on a different coin
  // than the user's saved preference (tradingCoin). Follow the live engine
  // coin so the selected button and header always match what's actually
  // being traded. Falls back to the saved preference when stopped.
  const displayCoin = (autonomousStatus?.is_running && liveBotCoin)
    ? liveBotCoin
    : tradingCoin;
  // True when the bot auto-rotated to a coin the user didn't pick — surfaced
  // as a small hint so the highlight change isn't itself confusing.
  const autoRotatedAway = Boolean(
    autonomousStatus?.is_running && liveBotCoin && liveBotCoin !== tradingCoin
  );
  const currentCoinMaxLev = Math.min(40, maxLevs[displayCoin] || 40);

  // Regime preview — peek at each coin's current ADX/regime so users
  // can pick a tradeable coin BEFORE clicking Start Trading. Refreshes
  // every 60s (server caches the same way), and on user-triggered
  // "refresh" via clicking the section label.
  const [regimePreview, setRegimePreview] = React.useState(null);
  const [regimeLoading, setRegimeLoading] = React.useState(false);
  const fetchRegimePreview = React.useCallback(async () => {
    setRegimeLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/grid-bot/regime-preview`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setRegimePreview(r.data);
    } catch (e) {
      // Silently degrade — coin selector still works without the hint
    } finally {
      setRegimeLoading(false);
    }
  }, [BACKEND_URL]);
  React.useEffect(() => {
    fetchRegimePreview();
    const id = setInterval(fetchRegimePreview, 60_000);
    return () => clearInterval(id);
  }, [fetchRegimePreview]);

  // Look up regime for a coin from the preview payload.
  // For the currently-traded coin, prefer the live bot regime (which
  // applies hysteresis) over the stateless preview — otherwise the
  // pill and the Hydra dashboard disagree on borderline ADX values.
  const regimeFor = (c) => {
    if (liveBotCoin && liveBotRegime && c === liveBotCoin) {
      // Try to keep the ADX/other metadata from the preview if present
      const preview = regimePreview?.coins?.find(x => x.coin === c) || {};
      return { ...preview, coin: c, regime: liveBotRegime, source: 'live' };
    }
    if (!regimePreview?.coins) return null;
    return regimePreview.coins.find(x => x.coin === c) || null;
  };
  // Style + label for the regime pill under each coin button
  const regimeStyle = (regime) => {
    if (regime === 'ranging') return { bg: 'bg-green-700/40', txt: 'text-green-300', label: 'RANGING' };
    if (regime === 'mild_trend') return { bg: 'bg-amber-700/40', txt: 'text-amber-300', label: 'MILD TREND' };
    if (regime === 'strong_trend') return { bg: 'bg-red-700/40', txt: 'text-red-300', label: 'STRONG TREND' };
    return { bg: 'bg-gray-700/40', txt: 'text-gray-400', label: '—' };
  };
  // Friendly-ify the raw stop reason for non-technical users.
  // "crashed" / "cancelled" → short, calm sentences. "manual" is filtered
  // out upstream in App.js so it never reaches this component.
  const stopBanner = (() => {
    if (!lastStopReason || !lastStopReason.reason) return null;
    const r = lastStopReason.reason;
    let title = "Trading stopped";
    let body = "Press Start Trading to resume.";
    if (r === "crashed") {
      title = "Auto-recovering from an unexpected error";
      body = "The bot hit an error and is restarting automatically. "
           + "No action needed — usually back online within 60 seconds.";
    } else if (r === "cancelled") {
      title = "Auto-recovering from a server restart";
      body = "The bot's task was cancelled by a server restart and is "
           + "restarting automatically. No action needed — usually back "
           + "online within 60 seconds.";
    } else if (r === "risk_guard_kill") {
      title = "Trading stopped by risk guard";
      body = "A safety guard stopped the bot. Review the detail below "
           + "and press Start Trading when you're ready to resume.";
    } else if (r === "api_wallet_expiring") {
      title = "API key expiring — trading stopped safely";
      body = "Your Hyperliquid API wallet was about to expire, so the bot "
           + "stopped and cancelled its orders while the key could still "
           + "sign. Generate a new API wallet at app.hyperliquid.xyz/API, "
           + "paste its private key in Settings, then press Start Trading.";
    } else if (r === "rate_limited") {
      title = "Hyperliquid throttled the wallet";
      body = "Hyperliquid is rate-limiting this wallet (429 — Too Many "
           + "Requests). Wait 10–15 minutes for the request budget to "
           + "recover, then press Start Trading.";
    }
    return { title, body, detail: lastStopReason.detail };
  })();

  return (
    <div className="bg-gray-800 rounded-lg p-4 md:p-6">
      <h2 className="text-lg md:text-xl font-bold mb-4">Control Panel</h2>
      {stopBanner && (
        <div
          data-testid="bot-stopped-banner"
          className="mb-4 rounded-md border border-amber-500/40 bg-amber-900/30 p-3 text-sm"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-semibold text-amber-200">
                {stopBanner.title}
              </div>
              <div className="text-amber-100/90 mt-0.5">
                {stopBanner.body}
              </div>
            </div>
            <button
              data-testid="bot-stopped-banner-dismiss"
              onClick={onDismissStopReason}
              className="text-amber-300/70 hover:text-amber-200 text-lg leading-none px-1"
              title="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}
      <div className="space-y-4">
        <div className="flex flex-col space-y-4">
          {/* Trading Status */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="flex items-center space-x-2">
              <span className="text-gray-300 font-medium">You:</span>
              <span
                data-testid="autonomous-trading-user-status"
                className={`font-bold ${
                  (isCooldownActive || isRegimeFilterPause)
                    ? 'text-orange-400'
                    : (autonomousStatus.user_participating ? 'text-green-400' : 'text-red-400')
                }`}
              >
                {autonomousDisplayStatus}
              </span>
              {isCooldownActive && (
                <span data-testid="autonomous-trading-cooldown-timer-inline" className="text-orange-300 font-semibold">
                  {formatCooldownTimer(cooldownRemainingSec)}
                </span>
              )}
            </div>
            {(() => {
              const missingSub = !subscriptionStatus?.has_active_subscription;
              const missingKeys = portfolio?.note === "No Hyperliquid keys configured";
              if (!autonomousStatus.user_participating && (missingSub || missingKeys)) {
                return (
                  <span className="text-sm font-medium text-orange-400">
                    {missingSub && missingKeys
                      ? "(Subscription and API keys required to trade)"
                      : missingSub
                        ? "(Subscription required to trade)"
                        : "(API keys required to trade)"}
                  </span>
                );
              }
              const now = Date.now();
              if (pollingBackoffUi.until && now < pollingBackoffUi.until) {
                const secs = Math.max(1, Math.ceil((pollingBackoffUi.until - now) / 1000));
                return (
                  <span className="text-xs font-normal text-gray-500" title="Transient network delay — bot continues trading normally">
                    (Reconnecting in ~{secs}s)
                  </span>
                );
              }
              return null;
            })()}
          </div>

          {isCooldownActive && (
            <div data-testid="autonomous-trading-cooldown-resume-text" className="text-sm font-medium text-orange-300">
              Resuming in {formatCooldownTimer(cooldownRemainingSec)}
            </div>
          )}

          {/* Trading Buttons */}
          <div className="flex flex-wrap items-center gap-4">
            {(() => {
              const missingSub = !subscriptionStatus?.has_active_subscription;
              const missingKeys = portfolio?.note === "No Hyperliquid keys configured";
              const isDisabled = missingSub || missingKeys || autonomousStatus.user_participating || isCooldownActive || autonomousLoading;
              return (
                <button
                  data-testid="autonomous-start-trading-button"
                  onClick={() => startAutonomousTrading()}
                  disabled={isDisabled}
                  className={`px-6 py-2 rounded font-semibold text-white transition-colors ${
                    isDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                  }`}
                  title={isDisabled ? "Resolve requirements to start" : "Start autonomous trading"}
                >
                  {autonomousLoading && !autonomousStatus.user_participating ? 'Starting...' : 'Start Trading'}
                </button>
              );
            })()}
            <button
              data-testid="autonomous-stop-trading-button"
              onClick={(e) => { e.stopPropagation(); stopAutonomousTrading(true); }}
              disabled={(!autonomousStatus.user_participating && !isCooldownActive) || autonomousLoading}
              className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed px-6 py-2 rounded font-semibold text-white"
              title="Stop the bot, cancel all open orders, and close any open positions"
            >
              {autonomousLoading && autonomousStatus.user_participating ? 'Stopping...' : 'Stop Trading'}
            </button>
            <button
              data-testid="autonomous-close-all-positions-button"
              onClick={(e) => { e.stopPropagation(); closeAllPositions(); }}
              disabled={closingPositions}
              className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed px-6 py-2 rounded font-semibold text-white"
            >
              {closingPositions ? 'Closing...' : 'Close All Positions'}
            </button>
          </div>

          {/* Coin Selector — controls which Hyperliquid perp the bot trades */}
          <div data-testid="coin-selector" className="border-t border-gray-700 pt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-300 font-medium" data-testid="coin-selector-label">
                Coin
              </span>
              <span className="text-xs text-gray-400" data-testid="coin-selector-value">
                Trading {displayCoin} · max leverage {currentCoinMaxLev}x
              </span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {coinList.map(c => {
                const r = regimeFor(c);
                const rs = regimeStyle(r?.regime);
                return (
                <div key={c} className="flex-1 min-w-[80px] flex flex-col gap-1 items-stretch">
                  <button
                    data-testid={`coin-btn-${c}`}
                    onClick={async () => {
                      if (autonomousStatus.is_running) return;
                      const newMax = Math.min(40, maxLevs[c] || 40);
                      const shouldClampLev = tradingLeverage > newMax;
                      setTradingCoin(c);
                      if (shouldClampLev) setTradingLeverage(newMax);
                      try {
                        await axios.post(`${BACKEND_URL}/api/grid-bot/trading-prefs`,
                          { coin: c },
                          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                        );
                      } catch {}
                    }}
                    disabled={autonomousStatus.is_running}
                    title={r ? `${c}: ADX ${r.adx} → ${rs.label}` : c}
                    className={`w-full py-2 px-1 rounded text-sm font-semibold transition-colors ${
                      displayCoin === c
                        ? 'bg-indigo-600 text-white ring-2 ring-indigo-400'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {c}
                  </button>
                  {/* Regime badge — rendered OUTSIDE the button so the
                      disabled:opacity-50 of the parent button (while bot
                      is running) doesn't fade it. Users specifically asked
                      for regime to stay visible during trading. */}
                  <span
                    data-testid={`coin-regime-${c}`}
                    className={`text-[9px] leading-none text-center px-1.5 py-0.5 rounded font-semibold ${rs.bg} ${rs.txt}`}
                  >
                    {rs.label}
                  </span>
                </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-500" data-testid="regime-preview-status">
                {regimeLoading
                  ? 'Loading regimes…'
                  : regimePreview?.fetched_at
                    ? `Regimes from Hyperliquid · refreshes every 60s`
                    : 'Regime preview unavailable'}
              </span>
              <button
                type="button"
                data-testid="regime-preview-refresh"
                onClick={fetchRegimePreview}
                disabled={regimeLoading}
                className="text-xs text-indigo-400 hover:text-indigo-300 disabled:text-gray-500 disabled:cursor-not-allowed"
              >
                Refresh
              </button>
            </div>
            {autonomousStatus.is_running && (
              <p className="text-xs text-gray-500 mt-1" data-testid="coin-selector-locked-msg">
                {autoRotatedAway
                  ? `Auto-rotated to ${displayCoin} (your pick: ${tradingCoin}). Stop the bot to switch coin.`
                  : 'Stop the bot to switch coin'}
              </p>
            )}

            {/* Auto-rotate toggle — hop to the best ranging coin when the
                current coin trends out (waits for a flat position first). */}
            <div
              className="mt-3 flex items-start justify-between gap-3 rounded bg-gray-900/40 px-3 py-2"
              data-testid="auto-rotate-row"
            >
              <div className="flex-1">
                <span className="text-sm text-gray-300 font-medium" data-testid="auto-rotate-label">
                  Auto-rotate to best ranging coin
                </span>
                <p className="text-xs text-gray-500 mt-0.5" data-testid="auto-rotate-desc">
                  When the current coin starts trending (paused), the bot waits
                  for a flat position, then switches to the strongest ranging
                  coin. Off by default.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={Boolean(autoRotateCoin)}
                data-testid="auto-rotate-toggle"
                onClick={(e) => { e.stopPropagation(); if (toggleAutoRotateCoin) toggleAutoRotateCoin(); }}
                disabled={autoRotateLoading}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                  autoRotateCoin ? 'bg-emerald-600' : 'bg-gray-600'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    autoRotateCoin ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Cut & Trail: in loss → instant taker dump; in profit → hold &
                exit when it drops from the peak. */}
            <div
              className="mt-3 flex items-start justify-between gap-3 rounded bg-gray-900/40 px-3 py-2"
              data-testid="cut-and-trail-row"
            >
              <div className="flex-1">
                <span className="text-sm text-gray-300 font-medium" data-testid="cut-and-trail-label">
                  Cut &amp; Trail exits (experimental)
                </span>
                <p className="text-xs text-gray-500 mt-0.5" data-testid="cut-and-trail-desc">
                  Cuts losers at the coin&apos;s optimized stop distance and
                  trails winners a third of the take-profit distance off their
                  peak — thresholds follow the grid&apos;s own geometry. Off by
                  default; applies on next Start.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={Boolean(cutAndTrail)}
                data-testid="cut-and-trail-toggle"
                onClick={(e) => { e.stopPropagation(); if (toggleCutAndTrail) toggleCutAndTrail(); }}
                disabled={cutAndTrailLoading}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                  cutAndTrail ? 'bg-emerald-600' : 'bg-gray-600'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    cutAndTrail ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Design-C adaptive-exit trial: derive arm/trail from the live
                peak distribution (zero hand-set constants). Requires Cut &
                Trail; arms the 8% equity kill-guard on enablement. */}
            <div
              className="mt-3 flex items-start justify-between gap-3 rounded border border-amber-700/40 bg-amber-900/10 px-3 py-2"
              data-testid="ct-adaptive-row"
            >
              <div className="flex-1">
                <span className="text-sm text-amber-200 font-medium" data-testid="ct-adaptive-label">
                  Adaptive Exit Trial — Design C (self-calibrating)
                </span>
                <p className="text-xs text-gray-400 mt-0.5" data-testid="ct-adaptive-desc">
                  Derives arm (pooled-peak P40) &amp; trail (loser-peak P60) from
                  your last 100 trips — no fixed thresholds to drift. Turning
                  this on records trial-start equity and arms an 8% kill-guard.
                  Pre-registered trial: 60 trips or 14 days. Applies on next Start.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={Boolean(ctAdaptive)}
                data-testid="ct-adaptive-toggle"
                onClick={(e) => { e.stopPropagation(); if (toggleCtAdaptive) toggleCtAdaptive(); }}
                disabled={ctAdaptiveLoading}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                  ctAdaptive ? 'bg-amber-500' : 'bg-gray-600'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    ctAdaptive ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Hybrid regime-aware exit: trend-aligned trades let profit run on
                a trailing stop (never close red); range trades keep the fast TP. */}
            <div
              className="mt-3 flex items-start justify-between gap-3 rounded bg-gray-900/40 px-3 py-2"
              data-testid="hybrid-exit-row"
            >
              <div className="flex-1">
                <span className="text-sm text-gray-300 font-medium" data-testid="hybrid-exit-label">
                  Hybrid Exit — let winners run (smart)
                </span>
                <p className="text-xs text-gray-500 mt-0.5" data-testid="hybrid-exit-desc">
                  When a trade opens with the trend, it lets profit run on a
                  trailing stop and locks breakeven — once green it can&apos;t
                  close red. Range trades keep the fast take-profit. Off by
                  default; applies on next Start.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={Boolean(hybridExit)}
                data-testid="hybrid-exit-toggle"
                onClick={(e) => { e.stopPropagation(); if (toggleHybridExit) toggleHybridExit(); }}
                disabled={hybridExitLoading}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                  hybridExit ? 'bg-emerald-600' : 'bg-gray-600'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    hybridExit ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
          <div className="mt-4 border-t border-gray-700 pt-4 space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-300 font-medium" data-testid="position-size-label">Position Size per Level</span>
                <span className="text-sm font-bold text-white" data-testid="position-size-value">${tradingNotional} x {tradingLeverage}x = ${tradingNotional * tradingLeverage} effective</span>
              </div>
              <div className="flex gap-2 flex-wrap">
                {[1, 5, 10, 25, 50, 100, 250, 500, 1000].map(val => (
                  <button
                    key={val}
                    data-testid={`notional-btn-${val}`}
                    onClick={async () => {
                      setTradingNotional(val);
                      try {
                        await axios.post(`${BACKEND_URL}/api/grid-bot/trading-prefs`,
                          { notional_per_level: val },
                          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                        );
                      } catch {}
                    }}
                    disabled={autonomousStatus.is_running}
                    className={`flex-1 min-w-[60px] py-2 rounded text-sm font-semibold transition-colors ${
                      tradingNotional === val
                        ? 'bg-blue-600 text-white ring-2 ring-blue-400'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    ${val}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-300 font-medium" data-testid="leverage-label">Leverage</span>
              </div>
              <div className="flex gap-2">
                {[10, 20, 30, 40].map(val => {
                  const overMax = val > currentCoinMaxLev;
                  return (
                  <button
                    key={val}
                    data-testid={`leverage-btn-${val}`}
                    onClick={async () => {
                      if (overMax) return;
                      setTradingLeverage(val);
                      try {
                        await axios.post(`${BACKEND_URL}/api/grid-bot/trading-prefs`,
                          { leverage: val },
                          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
                        );
                      } catch {}
                    }}
                    disabled={autonomousStatus.is_running || overMax}
                    title={overMax ? `${tradingCoin} max is ${currentCoinMaxLev}x on Hyperliquid` : ''}
                    className={`flex-1 py-2 rounded text-sm font-semibold transition-colors ${
                      tradingLeverage === val
                        ? 'bg-amber-600 text-white ring-2 ring-amber-400'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    } disabled:opacity-30 disabled:cursor-not-allowed`}
                  >
                    {val}x
                  </button>
                  );
                })}
              </div>
              {autonomousStatus.is_running && (
                <p className="text-xs text-gray-500 mt-1">Stop the bot to change position size or leverage</p>
              )}
            </div>
          </div>

          {/* Regime Toggles */}
          <div className="mt-4 border-t border-gray-700 pt-4 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              {[
                ['ranging', 'Ranging'],
                ['mild_trend', 'Mild Trend'],
                ['strong_trend', 'Strong Trend'],
              ].map(([regimeKey, regimeLabel]) => {
                const enabled = allowedTradeRegimes.includes(regimeKey);
                return (
                  <button
                    key={regimeKey}
                    type="button"
                    data-testid={`autonomous-regime-toggle-${regimeKey}`}
                    onClick={(e) => { e.stopPropagation(); toggleAllowedTradeRegime(regimeKey); }}
                    disabled={allowedTradeRegimesLoading}
                    className={`px-4 py-2 rounded font-semibold text-sm transition-colors ${enabled ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'} disabled:bg-gray-500 disabled:cursor-not-allowed`}
                  >
                    {enabled ? `Pause ${regimeLabel}` : `Resume ${regimeLabel}`}
                  </button>
                );
              })}
            </div>
            <div data-testid="autonomous-allowed-regimes-status" className="text-sm text-gray-300">
              {(() => {
                const allRegimes = ['ranging', 'mild_trend', 'strong_trend'];
                const pausedRegimes = allRegimes.filter((r) => !allowedTradeRegimes.includes(r));
                return `Paused regimes: ${pausedRegimes.length ? pausedRegimes.join(', ') : 'none'}`;
              })()}
              <span className="ml-2 text-xs text-gray-400">
                All regimes are active by default. Pausing a regime cancels open grid orders and blocks new entries in that regime. Existing positions stay open and exit via their own TP/SL.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ControlPanel;
